import ActionTextContentField from 'FlowBuilder/components/ActionViews/ActionTextContentField'
import ExpressButtons from './ExpressButtons'
import PaypalButton from './PaypalButton';
import useActionTranslate from 'FlowBuilder/hooks/useActionTranslate';
import currenciesData from 'FlowBuilder/lib/currenciesData';
import liquidEngine from 'FlowBuilder/components/ActionViews/liquidEngine'

export default function DonatePage({
  actionId,
  config,
  isMonthly,
  setIsMonthly,
  amount,
  setAmount,
  coverFee,
  setCoverFee,
  currency,
  setCurrency,
  displayAmount,
  coverFeeAmount,
  paymentAmount,
  handleSubmit,
  presetAmounts,
  stripePromise,
  onComplete,
  targetDetails,
  onUpdateNoTarget
}) {
  const [customInputAmount, setCustomInputAmount] = useState((amount / 100).toFixed(amount % 100 === 0 ? 0 : 2));
  const [animateHeart, setAnimateHeart] = useState(false); // State to trigger heart animation
  const currencies = config.currencies || ['gbp'];
  const currencyData = currenciesData.find(c => c.code === currency.toLowerCase());

  function handleInputChange(e) {
    const val = e.target.value.replace(/[^0-9.]/g, '');
    setCustomInputAmount(val);

    let parsedValue = parseFloat(val);
    if (isNaN(parsedValue)) parsedValue = 0;

    const rounded = Math.round((parsedValue + Number.EPSILON) * 100) / 100;

    setAmount(Math.round(rounded * 100));
  }

  const [showDisabledMessage, setShowDisabledMessage] = useState(false);
  const buttonDisabled = !paymentAmount || paymentAmount < currencyData.minimum || paymentAmount > currencyData.maximum
  // In reality this is typically 50cents or 30pence, but it depends on the default settlement currency of the connected stripe account
  // e..g if base currency is USD (min $0.50) but we try for 0.5AUD (roughly 0.35USD) it will be rejected
  // This means that any currency needs to convert to atleast that amount, which is complex to handle here, so we use 100 minor units as a simple universal minimum

  useEffect(() => {
    // 1.5 second timeout to show the disabled message
    if (buttonDisabled) {
      const timeout = setTimeout(() => {
        setShowDisabledMessage(true);
      }
        , 1500);
      return () => {
        clearTimeout(timeout);
        setShowDisabledMessage(false);
      }
    }
    setShowDisabledMessage(false);
  }, [buttonDisabled])

  // Update custom input amount when amount changes
  useEffect(() => {
    if (amount / 100 == parseFloat(customInputAmount)) return; // Prevent loop of custom setting amount setting custom and e.g. jumping from 2.1 to 2.10
    setCustomInputAmount((amount / 100).toFixed(amount % 100 === 0 ? 0 : 2));
  }, [amount]);

  useEffect(() => {
    if (isMonthly) {
      setAnimateHeart(true); // Trigger the animation when monthly is selected
    }
    if (!isMonthly && !config.preTickCoverFee) {
      setCoverFee(false); // Reset cover fee when switching to one-off if not pre-ticked
    }
  }, [isMonthly]);

  useEffect(() => {
    if (animateHeart) {
      const timeout = setTimeout(() => setAnimateHeart(false), 1000); // Match animation duration
      return () => clearTimeout(timeout);
    }
  }, [animateHeart]);


  const { fallback: {
    coverFeeText: coverFeeTextRaw,
    secureDonationText,
    giveOnceText,
    monthlyText,
    minimumAmountText: minimumAmountTextRaw,
    maximumAmountText: maximumAmountTextRaw,
    donateButtonText: donateButtonTextRaw,
    donateButtonTextMonthly: donateButtonTextMonthlyRaw,
    donatingAsText,
    notMeText,
  } } = useActionTranslate({ actionId });

  const coverFeeText = useMemo(() => liquidEngine.parseAndRenderSync(coverFeeTextRaw, { fee: displayAmount(coverFeeAmount) }), [coverFeeTextRaw, coverFeeAmount, currencyData]);
  const donateButtonText = useMemo(() => liquidEngine.parseAndRenderSync(donateButtonTextRaw, { amount: displayAmount(paymentAmount) }), [donateButtonTextRaw, paymentAmount, currencyData]);
  const donateButtonTextMonthly = useMemo(() => liquidEngine.parseAndRenderSync(donateButtonTextMonthlyRaw, { amount: displayAmount(paymentAmount) }), [donateButtonTextMonthlyRaw, paymentAmount, currencyData]);
  const minimumAmountText = useMemo(() => liquidEngine.parseAndRenderSync(minimumAmountTextRaw, { amount: displayAmount(currencyData.minimum) }), [minimumAmountTextRaw, currencyData]);
  const maximumAmountText = useMemo(() => liquidEngine.parseAndRenderSync(maximumAmountTextRaw, { amount: displayAmount(currencyData.maximum) }), [maximumAmountTextRaw, currencyData]);

  function AmountButton({ value }) {
    return (
      <button
        className={`amount-btn ${amount === value ? 'active' : ''}`}
        type="button"
        onClick={() => {
          setAmount(value)
        }}
      >
        {displayAmount(value)}
      </button>
    );
  }

  return <div className="donate-page">
    <div className="donation-header">
      <SvgIconsLock />
      <span>{secureDonationText}</span>
    </div>

    {config.enableMonthlyDonations && !config.disableOneOffDonations && (
      <>
        <div className="donation-type-toggle">
          <button
            type="button"
            className={`toggle-btn ${!isMonthly ? 'active' : ''}`}
            onClick={() => setIsMonthly(false)}
          >
            <span>{giveOnceText}</span>
          </button>
          <button
            type="button"
            className={`toggle-btn ${isMonthly ? 'active' : ''}`}
            onClick={() => setIsMonthly(true)}
          >
            <div className="heart-icon">
              <SvgIconsHeart />
              {animateHeart && <div className="heart-animation"><SvgIconsHeart /></div>}
            </div>
            <span>
              {monthlyText}
            </span>
          </button>
        </div>
      </>
    )}

    {presetAmounts && presetAmounts.length > 0 && (
      <>
        <div className="amount-container">
          {presetAmounts.map((value) => (
            <AmountButton key={value} value={value} />
          ))}
        </div>
      </>
    )}

    <div className="custom-amount">
      <div className="input-group">
        <span className="currency">{currencyData.symbol}</span>
        <input
          className="custom-amount-input"
          type="text"
          value={customInputAmount}
          onChange={handleInputChange}
          placeholder="Custom amount"
        />
        {currencies.length > 1 && <select
          className="currency-select"
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
        >
          {currencies.map(code => <option value={code} key={code}>{code.toUpperCase()}</option>)}
        </select>}
      </div>
    </div>

    {config.coverFee && (isMonthly || !config.enableMonthlyUpsell || config.preTickCoverFee) &&
      <div className="cover-fee">
        <label><input type="checkbox" checked={coverFee} onChange={() => setCoverFee(!coverFee)} /><ActionTextContentField html={coverFeeText} actionId={actionId} slug='coverFeeText' /></label>
      </div>
    }

    {targetDetails?.signedIn && (
      <div className="not-me-container margin-top">
        <label>{donatingAsText} {targetDetails.signedInAs}</label>
        <div className='not-me-link' onClick={onUpdateNoTarget}>{notMeText}</div>
      </div>
    )}

    <div>
      <button
        className="donate-btn"
        type="button"
        onClick={handleSubmit}
        disabled={buttonDisabled}
      >
        {isMonthly ? donateButtonTextMonthly : donateButtonText}
      </button>
    </div>

    <div className='margin-top'>
      {!buttonDisabled && <ExpressButtons amountInMinorUnits={paymentAmount} currency={currency} isMonthly={isMonthly} coverFee={coverFee} onComplete={onComplete} stripePromise={stripePromise} />}
      {!buttonDisabled && <PaypalButton amountInMinorUnits={paymentAmount} currency={currency} isMonthly={isMonthly} coverFee={coverFee} onComplete={onComplete} stripePromise={stripePromise} />}
    </div>

    {showDisabledMessage &&
      <div className='center margin-top disabled-message'>
        {(!paymentAmount || paymentAmount < currencyData.minimum) ? minimumAmountText : maximumAmountText}
      </div>
    }
  </div>
}
